import React, { useState, useEffect, useMemo } from 'react';
import { LineChart } from '@mui/x-charts/LineChart';
import { Grid, Typography, Box, CircularProgress, FormGroup, FormControlLabel, Checkbox } from '@mui/material';
import { pastelLineChartPalette } from './colors';

const SERIES_KEY_ORDER = [
    'DSP_SH',
    'SSP_SH',
    'DSP_RX',
    'SSP_RX',
    'DSP_SUM',
    'SSP_SUM',
];

function colorForSeriesKey(key) {
    const i = SERIES_KEY_ORDER.indexOf(key);
    const idx = i >= 0 ? i : 0;
    return pastelLineChartPalette[idx % pastelLineChartPalette.length];
}

/**
 * @param {Array<{ key: string, label: string, data: number[] }>} series
 * @param {string[]} dateLabels
 * @param {string} nameTitle
 */
const LineRevenueChart = ({ series = [], dateLabels = [], nameTitle }) => {
    const [loading, setLoading] = useState(false);
    const [visible, setVisible] = useState({});

    const seriesValid = useMemo(() => {
        if (!dateLabels?.length || !series?.length) {
            return false;
        }
        return series.every((s) => s.data && s.data.length === dateLabels.length);
    }, [series, dateLabels]);

    useEffect(() => {
        if (!seriesValid) {
            setLoading(true);
        } else {
            setLoading(false);
        }
    }, [seriesValid]);

    useEffect(() => {
        if (!series.length) {
            return;
        }
        setVisible(Object.fromEntries(series.map((s) => [s.key, true])));
    }, [dateLabels, series]);

    const activeSeries = useMemo(() => {
        return series.filter((s) => visible[s.key] !== false);
    }, [series, visible]);

    const chartSeries = activeSeries.length ? activeSeries : series;

    const handleToggle = (key, checked) => {
        const visibleCount = series.filter((s) => visible[s.key] !== false).length;
        if (!checked && visibleCount <= 1) {
            return;
        }
        setVisible((v) => ({ ...v, [key]: checked }));
    };

    return (
        <div>
            <Typography
                variant="h5"
                component="h5"
                align="left"
                sx={{
                    padding: 1,
                    paddingBottom: 4,
                }}
            >
                {nameTitle}
            </Typography>
            {!loading && series.length > 0 && (
                <Box
                    sx={{
                        mx: 1,
                        mb: 2,
                        pt: 0.5,
                        display: 'flex',
                        justifyContent: 'center',
                    }}
                >
                    <FormGroup
                        row
                        sx={{
                            flexWrap: 'wrap',
                            gap: 0.5,
                            justifyContent: 'center',
                        }}
                    >
                        {series.map((s) => {
                            const lineColor = colorForSeriesKey(s.key);
                            return (
                                <FormControlLabel
                                    key={s.key}
                                    control={
                                        <Checkbox
                                            size="small"
                                            checked={visible[s.key] !== false}
                                            onChange={(e) => handleToggle(s.key, e.target.checked)}
                                            sx={{
                                                color: lineColor,
                                                '&.Mui-checked': { color: lineColor },
                                            }}
                                        />
                                    }
                                    label={s.label}
                                    sx={{ mr: 1 }}
                                />
                            );
                        })}
                    </FormGroup>
                </Box>
            )}
            {loading ? (
                <Grid item xs={12} md={12}>
                    <Box justifyContent="center" textAlign="center" alignItems="center">
                        <CircularProgress size={300} />
                    </Box>
                </Grid>
            ) : (
                <LineChart
                    series={chartSeries.map((s) => ({
                        data: s.data,
                        label: s.label,
                        id: s.key,
                        color: colorForSeriesKey(s.key),
                    }))}
                    xAxis={[{ scaleType: 'point', data: dateLabels }]}
                    margin={{ left: 65 }}
                    slotProps={{ legend: { hidden: true } }}
                    width="700"
                    height="400"
                />
            )}
        </div>
    );
};

export default LineRevenueChart;
